import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Coins, Lock, Check, Sparkles, Star, Crown, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

// Military-themed emoji/icon mappings for each collectible
// Dogs now use actual images
const DOG_IMAGES = {
  dog_scout: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/7zg1rilz_Gemini_Generated_Image_ucbz1lucbz1lucbz.png',
  dog_patrol: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/z54zlaj8_Gemini_Generated_Image_up10mdup10mdup10.png',
  dog_guardian: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/dhbgy5ba_Gemini_Generated_Image_taiufmtaiufmtaiu.png',
  dog_tank: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/k1u3nixq_Gemini_Generated_Image_rv0hsfrv0hsfrv0h.png',
  dog_stealth: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/fs7sjze3_Gemini_Generated_Image_xs3dvixs3dvixs3d.png',
  dog_commander: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/6lp2hxqd_Gemini_Generated_Image_1fjtiz1fjtiz1fjt.png',
};

// Dolls now use actual images
const DOLL_IMAGES = {
  doll_penny: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/74fbg224_2.jpeg',
  doll_molly: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/m5o3hc2s_3.jpeg',
  doll_riley: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/4jo6qeb0_4.jpeg',
  doll_piper: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/1jxz7s5r_5.jpeg',
  doll_elle: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/ekxskcvo_6.jpeg',
  doll_gigi: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/op5d6amp_WhatsApp%20Image%202026-03-04%20at%2010.21.13%20AM.jpeg',
};

// Cats now use actual images - tactical ninja cats
const CAT_IMAGES = {
  cat_ninja: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/s0l1o075_Gemini_Generated_Image_bkx966bkx966bkx9.png',
  cat_sniper: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/vegt2979_Gemini_Generated_Image_8ejg558ejg558ejg.png',
  cat_techie: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/ymi04sqt_Gemini_Generated_Image_mq1dl2mq1dl2mq1d.png',
  cat_lookout: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/hn7oovdb_Gemini_Generated_Image_bi6llgbi6llgbi6l.png',
  cat_pilot: 'https://customer-assets.emergentagent.com/job_0e4a2968-ed3e-4331-a029-4f720e5976d0/artifacts/xg904xsn_Gemini_Generated_Image_ujm3ziujm3ziujm3.png',
  cat_admiral: 'https://customer-assets.emergentagent.com/job_d0f367d9-dfdd-4999-8182-1c85fd283ca6/artifacts/k67nyf5b_Gemini_Generated_Image_30nj130nj130nj13.png',
};

const COLLECTIBLE_ICONS = {
  // Dogs - with military accessories
  dog_scout: { emoji: '🐕', accessory: '🎖️', color: 'from-green-400 to-emerald-500', image: DOG_IMAGES.dog_scout },
  dog_patrol: { emoji: '🐕‍🦺', accessory: '🦺', color: 'from-amber-400 to-orange-500', image: DOG_IMAGES.dog_patrol },
  dog_guardian: { emoji: '🦮', accessory: '🕶️', color: 'from-blue-400 to-indigo-500', image: DOG_IMAGES.dog_guardian },
  dog_tank: { emoji: '🐶', accessory: '⛑️', color: 'from-slate-400 to-slate-600', image: DOG_IMAGES.dog_tank },
  dog_stealth: { emoji: '🐕', accessory: '🥷', color: 'from-gray-700 to-black', image: DOG_IMAGES.dog_stealth },
  dog_commander: { emoji: '🐺', accessory: '👑', color: 'from-yellow-400 to-amber-500', image: DOG_IMAGES.dog_commander },
  
  // Cats - with military gear (now with actual images)
  cat_ninja: { emoji: '🐈‍⬛', accessory: '🌙', color: 'from-purple-500 to-indigo-600', image: CAT_IMAGES.cat_ninja },
  cat_sniper: { emoji: '🐱', accessory: '🎯', color: 'from-green-500 to-teal-600', image: CAT_IMAGES.cat_sniper },
  cat_techie: { emoji: '🐈', accessory: '💡', color: 'from-cyan-400 to-blue-500', image: CAT_IMAGES.cat_techie },
  cat_lookout: { emoji: '🐱', accessory: '🔭', color: 'from-orange-400 to-red-500', image: CAT_IMAGES.cat_lookout },
  cat_pilot: { emoji: '😺', accessory: '✈️', color: 'from-sky-400 to-blue-500', image: CAT_IMAGES.cat_pilot },
  cat_admiral: { emoji: '🐱', accessory: '⚓', color: 'from-blue-600 to-indigo-700', image: CAT_IMAGES.cat_admiral },
  
  // Dolls - plush characters with images
  doll_penny: { emoji: '🐘', accessory: '🎖️', color: 'from-slate-400 to-slate-600', image: DOLL_IMAGES.doll_penny },
  doll_molly: { emoji: '🐘', accessory: '❤️‍🩹', color: 'from-rose-400 to-rose-600', image: DOLL_IMAGES.doll_molly },
  doll_riley: { emoji: '🐰', accessory: '📻', color: 'from-blue-400 to-cyan-500', image: DOLL_IMAGES.doll_riley },
  doll_piper: { emoji: '🦓', accessory: '🛩️', color: 'from-slate-700 to-slate-900', image: DOLL_IMAGES.doll_piper },
  doll_elle: { emoji: '🐙', accessory: '🔧', color: 'from-orange-400 to-red-500', image: DOLL_IMAGES.doll_elle },
  doll_gigi: { emoji: '🐵', accessory: '⭐', color: 'from-amber-600 to-amber-800', image: DOLL_IMAGES.doll_gigi },
};

// Category display info
const CATEGORY_INFO = {
  dogs: {
    title: 'Military Dogs',
    subtitle: 'Brave pups ready for action!',
    icon: '🐕',
    bgGradient: 'from-amber-100 to-orange-100',
    borderColor: 'border-amber-300'
  },
  cats: {
    title: 'Tactical Cats',
    subtitle: 'Stealthy felines on a mission!',
    icon: '🐱',
    bgGradient: 'from-purple-100 to-indigo-100',
    borderColor: 'border-purple-300'
  },
  dolls: {
    title: 'Hero Dolls',
    subtitle: 'Cute soldiers with big hearts!',
    icon: '🧸',
    bgGradient: 'from-pink-100 to-rose-100',
    borderColor: 'border-pink-300'
  }
};

// Collectible Card Component
const CollectibleCard = ({ item, onPurchase, purchasing, userCoins, onImageClick }) => {
  const iconData = COLLECTIBLE_ICONS[item.id] || { emoji: '🎁', accessory: '✨', color: 'from-slate-400 to-slate-500' };
  const canAfford = userCoins >= item.cost;
  const isSpecial = item.special;
  
  const handleImageClick = (e) => {
    e.stopPropagation();
    if (iconData.image) {
      onImageClick({ image: iconData.image, name: item.name, breed: item.breed, role: item.role, description: item.description });
    }
  };
  
  return (
    <div 
      className={`relative bg-white rounded-2xl border-2 overflow-hidden transition-all duration-300 
        ${item.owned ? 'border-green-400 shadow-green-100' : canAfford ? 'border-slate-200 hover:border-primary hover:shadow-lg hover:-translate-y-1' : 'border-slate-200 opacity-70'}
        ${isSpecial ? 'ring-2 ring-amber-400 ring-offset-2' : ''}
      `}
      data-testid={`collectible-${item.id}`}
    >
      {/* Special Badge */}
      {isSpecial && (
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-400 to-yellow-400 text-center py-1">
          <span className="text-xs font-bold text-amber-900 flex items-center justify-center gap-1">
            <Crown className="w-3 h-3" /> 12-Week Special!
          </span>
        </div>
      )}
      
      {/* Owned Badge */}
      {item.owned && (
        <div className="absolute top-2 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg z-10">
          <Check className="w-5 h-5 text-white" strokeWidth={3} />
        </div>
      )}
      
      {/* Main Content */}
      <div className={`p-4 ${isSpecial ? 'pt-8' : ''}`}>
        {/* Collectible Icon/Image - Clickable for zoom */}
        {iconData.image ? (
          // Display actual image for items with images - clickable to zoom
          <div 
            className="w-24 h-24 mx-auto mb-3 rounded-2xl overflow-hidden shadow-lg border-2 border-slate-200 cursor-zoom-in hover:scale-105 transition-transform"
            onClick={handleImageClick}
          >
            <img 
              src={iconData.image} 
              alt={item.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          // Fallback to emoji icons
          <div className={`w-20 h-20 mx-auto mb-3 rounded-2xl bg-gradient-to-br ${iconData.color} flex items-center justify-center relative shadow-lg`}>
            <span className="text-4xl">{iconData.emoji}</span>
            <span className="absolute -bottom-1 -right-1 text-xl bg-white rounded-full p-1 shadow-md">
              {iconData.accessory}
            </span>
          </div>
        )}
        
        {/* Info */}
        <div className="text-center">
          <h3 className="font-bold text-slate-900 text-lg">{item.name}</h3>
          {item.breed && <p className="text-xs text-slate-500 font-medium">{item.breed}</p>}
          {item.role && <p className="text-xs text-slate-500 font-medium">{item.role}</p>}
          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{item.description}</p>
        </div>
        
        {/* Tier Badge */}
        <div className="flex justify-center mt-2">
          <div className="flex items-center gap-0.5">
            {[...Array(item.tier || 1)].map((_, i) => (
              <Star 
                key={i} 
                className={`w-3 h-3 ${item.owned ? 'text-amber-500 fill-amber-400' : 'text-slate-300 fill-slate-200'}`} 
              />
            ))}
          </div>
        </div>
        
        {/* Price & Buy Button */}
        <div className="mt-3">
          {item.owned ? (
            <div className="w-full py-2.5 bg-green-100 rounded-xl text-center">
              <span className="text-green-700 font-bold flex items-center justify-center gap-1">
                <ShieldCheck className="w-4 h-4" /> In Your Collection!
              </span>
            </div>
          ) : (
            <button
              onClick={() => onPurchase(item)}
              disabled={!canAfford || purchasing}
              className={`w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all
                ${canAfford 
                  ? 'bg-gradient-to-r from-primary to-amber-400 text-black hover:shadow-md active:scale-95' 
                  : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                }`}
              data-testid={`buy-${item.id}`}
            >
              {!canAfford ? (
                <>
                  <Lock className="w-4 h-4" />
                  {item.cost} coins needed
                </>
              ) : (
                <>
                  <Coins className="w-4 h-4" />
                  Buy for {item.cost} coins
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Category Section Component
const CategorySection = ({ category, items, onPurchase, purchasing, userCoins, onImageClick }) => {
  const info = CATEGORY_INFO[category];
  
  return (
    <div className="mb-8">
      {/* Category Header */}
      <div className={`bg-gradient-to-r ${info.bgGradient} rounded-2xl p-4 mb-4 border-2 ${info.borderColor}`}>
        <div className="flex items-center gap-3">
          <span className="text-4xl">{info.icon}</span>
          <div>
            <h2 className="text-xl font-bold text-slate-900 font-fredoka">{info.title}</h2>
            <p className="text-slate-600 text-sm">{info.subtitle}</p>
          </div>
        </div>
      </div>
      
      {/* Items Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {items.map((item) => (
          <CollectibleCard
            key={item.id}
            item={item}
            onPurchase={onPurchase}
            purchasing={purchasing}
            userCoins={userCoins}
            onImageClick={onImageClick}
          />
        ))}
      </div>
    </div>
  );
};

const CollectionStorePage = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [storeData, setStoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [zoomedImage, setZoomedImage] = useState(null);

  useEffect(() => {
    fetchStoreData();
  }, []);

  const fetchStoreData = async () => {
    try {
      const response = await api.get('/collection-store');
      setStoreData(response.data);
    } catch (error) {
      console.error('Failed to fetch store data:', error);
      toast.error('Could not load the Collection Store');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (item) => {
    setSelectedItem(item);
  };

  const confirmPurchase = async () => {
    if (!selectedItem) return;
    
    setPurchasing(true);
    try {
      // Find the category
      let category = null;
      for (const [cat, items] of Object.entries(storeData.store)) {
        if (items.some(i => i.id === selectedItem.id)) {
          category = cat;
          break;
        }
      }
      
      const response = await api.post('/collection-store/buy', {
        item_id: selectedItem.id,
        category: category
      });
      
      toast.success(
        <div className="flex items-center gap-2">
          <span className="text-2xl">{COLLECTIBLE_ICONS[selectedItem.id]?.emoji || '🎉'}</span>
          <span>{response.data.message}</span>
        </div>,
        { duration: 4000 }
      );
      
      await fetchStoreData();
      await refreshUser();
      setSelectedItem(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not complete purchase');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce-soft mb-4">🎖️</div>
          <p className="text-xl text-slate-600">Loading Collection Store...</p>
        </div>
      </div>
    );
  }

  const userCoins = storeData?.coins || 0;
  const ownedCount = storeData?.owned_pets_count || 0;
  const totalAvailable = storeData?.total_pets_available || 18;

  // Filter items based on active tab
  const getFilteredCategories = () => {
    if (!storeData?.store) return {};
    if (activeTab === 'all') return storeData.store;
    return { [activeTab]: storeData.store[activeTab] };
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="collection-store-page">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6 text-slate-600" />
            </button>
            <h1 className="text-xl font-bold text-slate-900 font-fredoka">Collection Store</h1>
          </div>
          
          <div className="flex items-center gap-2 bg-primary/20 px-4 py-2 rounded-full" data-testid="coin-display">
            <Coins className="w-5 h-5 text-primary" />
            <span className="font-bold text-slate-900">{userCoins}</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Welcome Banner */}
        <div className="card-bubbly bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 mb-6">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-white font-fredoka mb-2">
                Welcome to the Collection Store! 🎖️
              </h2>
              <p className="text-white/90 mb-3">
                Collect adorable military-themed characters! Each one comes with special gear and accessories.
              </p>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-full">
                <Sparkles className="w-5 h-5 text-amber-300" />
                <span className="text-white font-bold">{ownedCount} / {totalAvailable} Collected</span>
              </div>
            </div>
            <div className="text-6xl md:text-7xl">
              {ownedCount >= 15 ? '🏆' : ownedCount >= 9 ? '⭐' : ownedCount >= 3 ? '🎖️' : '🎯'}
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'all', label: 'All', icon: '✨' },
            { id: 'dogs', label: 'Dogs', icon: '🐕' },
            { id: 'cats', label: 'Cats', icon: '🐱' },
            { id: 'dolls', label: 'Dolls', icon: '🧸' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 whitespace-nowrap transition-all
                ${activeTab === tab.id 
                  ? 'bg-primary text-black shadow-md' 
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-primary'
                }`}
              data-testid={`tab-${tab.id}`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Categories */}
        {Object.entries(getFilteredCategories()).map(([category, items]) => (
          <CategorySection
            key={category}
            category={category}
            items={items}
            onPurchase={handlePurchase}
            purchasing={purchasing}
            userCoins={userCoins}
            onImageClick={setZoomedImage}
          />
        ))}

        {/* Collection Progress */}
        <div className="card-bubbly mt-6">
          <h3 className="text-lg font-bold text-slate-900 font-fredoka mb-4">
            Collection Progress
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="w-12 h-12 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-2">
                <span className="text-2xl">🐕</span>
              </div>
              <p className="font-bold text-slate-900">
                {storeData?.store?.dogs?.filter(i => i.owned).length || 0}/6
              </p>
              <p className="text-xs text-slate-500">Dogs</p>
            </div>
            <div>
              <div className="w-12 h-12 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-2">
                <span className="text-2xl">🐱</span>
              </div>
              <p className="font-bold text-slate-900">
                {storeData?.store?.cats?.filter(i => i.owned).length || 0}/6
              </p>
              <p className="text-xs text-slate-500">Cats</p>
            </div>
            <div>
              <div className="w-12 h-12 mx-auto bg-pink-100 rounded-full flex items-center justify-center mb-2">
                <span className="text-2xl">🧸</span>
              </div>
              <p className="font-bold text-slate-900">
                {storeData?.store?.dolls?.filter(i => i.owned).length || 0}/6
              </p>
              <p className="text-xs text-slate-500">Dolls</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary via-amber-400 to-green-400 rounded-full transition-all duration-500"
                style={{ width: `${(ownedCount / totalAvailable) * 100}%` }}
              />
            </div>
            <p className="text-sm text-slate-500 text-center mt-2">
              {totalAvailable - ownedCount} more to complete your collection!
            </p>
          </div>
        </div>

        {/* Purchase Confirmation Modal */}
        {selectedItem && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="purchase-modal">
            <div className="bg-white rounded-3xl p-6 w-full max-w-sm animate-pop">
              <div className="text-center mb-6">
                {/* Item Preview - Show image if available */}
                {COLLECTIBLE_ICONS[selectedItem.id]?.image ? (
                  <div className="w-28 h-28 mx-auto mb-4 rounded-2xl overflow-hidden shadow-lg border-2 border-slate-200">
                    <img 
                      src={COLLECTIBLE_ICONS[selectedItem.id].image} 
                      alt={selectedItem.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className={`w-24 h-24 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${COLLECTIBLE_ICONS[selectedItem.id]?.color || 'from-slate-400 to-slate-500'} flex items-center justify-center relative shadow-lg`}>
                    <span className="text-5xl">{COLLECTIBLE_ICONS[selectedItem.id]?.emoji || '🎁'}</span>
                    <span className="absolute -bottom-2 -right-2 text-2xl bg-white rounded-full p-1.5 shadow-md">
                      {COLLECTIBLE_ICONS[selectedItem.id]?.accessory || '✨'}
                    </span>
                  </div>
                )}
                
                <h3 className="font-bold text-2xl text-slate-900 font-fredoka">{selectedItem.name}</h3>
                {selectedItem.breed && <p className="text-slate-500">{selectedItem.breed}</p>}
                {selectedItem.role && <p className="text-slate-500">{selectedItem.role}</p>}
                <p className="text-slate-600 mt-2">{selectedItem.description}</p>
              </div>

              {/* Price Info */}
              <div className="bg-slate-50 rounded-2xl p-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Price:</span>
                  <span className="font-bold text-lg flex items-center gap-1">
                    <Coins className="w-5 h-5 text-primary" />
                    {selectedItem.cost} coins
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-slate-600">Your balance:</span>
                  <span className="font-bold">{userCoins} coins</span>
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-200">
                  <span className="text-slate-600">After purchase:</span>
                  <span className={`font-bold ${userCoins - selectedItem.cost >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {userCoins - selectedItem.cost} coins
                  </span>
                </div>
              </div>

              {/* Special Item Notice */}
              {selectedItem.special && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-4 text-center">
                  <Crown className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                  <p className="text-sm text-amber-700 font-medium">
                    This is a special 12-Week reward character!
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedItem(null)}
                  className="flex-1 py-3 bg-slate-100 rounded-2xl font-bold text-slate-700 hover:bg-slate-200 transition-colors"
                  disabled={purchasing}
                  data-testid="cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPurchase}
                  disabled={purchasing || userCoins < selectedItem.cost}
                  className={`flex-1 py-3 rounded-2xl font-bold transition-all flex items-center justify-center gap-2
                    ${userCoins >= selectedItem.cost 
                      ? 'bg-gradient-to-r from-primary to-amber-400 text-black hover:shadow-md active:scale-95' 
                      : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    }`}
                  data-testid="confirm-purchase-btn"
                >
                  {purchasing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Buying...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Add to Collection!
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Image Zoom Modal */}
        {zoomedImage && (
          <div 
            className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50 cursor-zoom-out"
            onClick={() => setZoomedImage(null)}
            data-testid="zoom-modal"
          >
            <div className="relative max-w-2xl w-full animate-pop">
              {/* Close hint */}
              <p className="text-white/60 text-center text-sm mb-4">Tap anywhere to close</p>
              
              {/* Large Image */}
              <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={zoomedImage.image} 
                  alt={zoomedImage.name}
                  className="w-full h-auto object-contain"
                />
                
                {/* Character Info */}
                <div className="p-6 bg-gradient-to-t from-slate-100 to-white">
                  <h2 className="text-2xl font-bold text-slate-900 font-fredoka text-center">
                    {zoomedImage.name}
                  </h2>
                  {zoomedImage.breed && (
                    <p className="text-slate-500 text-center font-medium">{zoomedImage.breed}</p>
                  )}
                  {zoomedImage.role && (
                    <p className="text-slate-500 text-center font-medium">{zoomedImage.role}</p>
                  )}
                  {zoomedImage.description && (
                    <p className="text-slate-600 text-center mt-2">{zoomedImage.description}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CollectionStorePage;
